/* eslint-disable max-depth */
import getIn from 'lodash/get';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { push } from 'react-router-redux';
import { call, delay, fork, put, race, select, take } from 'redux-saga/effects';

import { getPageNormalizedOffset } from '../../../../../lib/RandomAccessUtils';
import { isNowPlayingPlaylistRoute } from '../../../../../lib/RouteUtils/PlaybackRouteUtils';
import { taskManagerSpawnFactory } from '../../../../../sagas/Helpers/SpawnedTaskManager';
import Logger from '../../../../../tools/Logger';
import { DOMAIN_NAME, LinkedStationPlaylist, messages, PLAYLIST_PAGE_SIZE } from '../../constants';
import * as D from '../../deps';
import {
  annotatePlaylists as annotatePlaylistsService,
  appendItems,
  create,
  deleteTracks,
  editTracks,
  getPlaylistAutofillTracks,
  getPlaylistRecommendations,
  getTracks,
  setDetails,
} from '../../services';
import {
  actionsMatch,
  createPlaylistTrackMoves,
  detailsDto,
  insertTrackDto,
  moveTrackDto,
  unlockedPlaylistDto,
} from '../../utils';

const spawn = taskManagerSpawnFactory(DOMAIN_NAME);
const log = Logger.child({
  component: 'domains/playback/playlistT3/sagas/workers',
});

function* tryGetTracksForPlaylist(pandoraId, version, offset, limit) {
  yield D.tryCall(getTracksForPlaylist, {
    data: {
      pandoraId,
      version,
      offset,
      limit,
    },
  });
}

export function* annotatePlaylists(action) {
  const args = {
    pandoraIds: action.data.pandoraIds,
  };
  const response = yield D.tryCall(annotatePlaylistsService, args);
  if (!response) throw new Error('annotatePlaylists failed');
}

export function* appendItemsToPlaylist(action) {
  const args = {
    pandoraId: action.data.pandoraId,
    playlistVersion: action.data.playlistVersion,
    itemPandoraIds: action.data.itemPandoraIds,
  };

  const response = yield D.tryCall(appendItems, args);
  if (response) {
    const totalTracks = response.totalTracks;
    yield handleResponseAndRefresh(response, 'appendItems', totalTracks - 1);
  }
}

export function* createPlaylist(action) {
  action.data.name =
    action.data.name || D.Constants.PlaylistT3.PlaylistConstants.DEFAULT_PLAYLIST_TITLE;
  const args = {
    details: detailsDto(
      action.data.name,
      action.data.description,
      action.data.secret,
      action.data.isPrivate,
      action.data.itemPandoraIds,
    ),
  };
  const response = yield D.tryCall(create, args);
  if (!response) throw new Error('createPlaylist failed');

  if (response.tempPandoraId) {
    yield call(
      tryGetTracksForPlaylist,
      response.tempPandoraId,
      response.entities.playlists[response.tempPandoraId].version - 1,
    );
    yield updateRecommendationsForPlaylist(response.tempPandoraId);
  } else {
    const message = response.message ? response.message : 'none';
    throw new Error('CreatePlaylist returned with error: ' + message);
  }
}

export function* setPlaylistDetails(action) {
  const args = {
    request: {
      pandoraId: action.data.pandoraId,
      playlistVersion: action.data.version,
      details: detailsDto(
        action.data.name,
        action.data.description,
        action.data.secret,
        action.data.isPrivate,
      ),
    },
  };
  const response = yield D.tryCall(setDetails, args);
  if (!response) throw new Error('setPlaylistDetails failed');
}

export function* unlockPlaylistAndNotify({ data: { playlist } }) {
  const args = {
    request: {
      pandoraId: playlist.get('pandoraId'),
      playlistVersion: playlist.get('version'),
      details: unlockedPlaylistDto(),
    },
  };
  const response = yield D.tryCall(setDetails, args);
  const playlistDetails = response.entities.playlists[playlist.get('pandoraId')];
  if (playlistDetails) {
    const playlist_id = playlist.get('pandoraId');
    const name = playlistDetails.name;
    const playlist_linked_type = LinkedStationPlaylist.PLAYLIST_LINKED_TYPE;
    const playlist_create_source_id = LinkedStationPlaylist.PLAYLIST_CREATE_SOURCE_ID;
    const created = true;
    yield put(
      D.getInDomain('analytics.actions.logPremiumPlaylistUnlocked')({
        playlist_id,
        name,
        playlist_linked_type,
        playlist_create_source_id,
        created,
      }),
    );
    yield call(
      D.putAndWait,
      D.Actions.Application.openLinkedPlaylistUnlockedModal({
        station_id: playlist.get('linkedSourceId'),
        playlist,
      }),
    );
  }
}

export function* deletePlaylist(action) {
  const { pandoraId, redirectToMyMusicPlaylists } = action.data;
  const { dispatch } = D.getStore();
  const playlist = yield select(D.Utils.Entity.getAnnotationsById, pandoraId);
  const playlistName = playlist.get('name');
  const playlistBodyComponent = (
    <FormattedMessage {...messages.deletePlaylistBody} values={{ playlistName }} />
  );
  const modalParams = {
    modalType: 'ModalStandard',
    title: <FormattedMessage {...messages.deletePlaylistTitle} />,
    body: playlistBodyComponent,
    onClose: () => {
      dispatch(D.Actions.Modal.closeModal());
    },
    hideXRow: true,
    buttons: [
      <D.ButtonT3
        key={0}
        theme={D.ButtonT3Theme.LIGHT_TRANSPARENT}
        isInline={true}
        onClick={() => {
          dispatch(D.Actions.Modal.closeModal());
        }}
      >
        <FormattedMessage {...messages.cancel} />
      </D.ButtonT3>,
      <D.ButtonT3
        key={1}
        isInline={true}
        qaTag={D.Constants.QATags.MDPC}
        onClick={() => {
          dispatch(D.Actions.Modal.closeModal());
          dispatch(
            D.Actions.Playlist.confirmDeletePlaylist({
              pandoraId,
              redirectToMyMusicPlaylists,
            }),
          );
        }}
      >
        <FormattedMessage {...messages.delete} />
      </D.ButtonT3>,
    ],
  };
  yield put(D.Actions.Modal.openModal(modalParams));
}

export function* confirmDeletePlaylist(action) {
  yield put(
    D.Actions.Collection.removeCollectionItem({
      pandoraId: getIn(action, 'data.pandoraId', null),
      redirectToMyMusicPlaylists: getIn(action, 'data.redirectToMyMusicPlaylists', false),
    }),
  );
}

export function* handleOutOfSyncUpdatesForLinkedPlaylists(action) {
  const playlistId = getIn(action, ['params', 'content', 'request', 'pandoraId'], null);
  const newVersion = getIn(
    action,
    ['response', 'entities', 'playlists', playlistId, 'version'],
    null,
  );
  const oldPlaylist = yield select(D.Utils.Music.getPlaylistFromState, playlistId);
  // oldPlaylist can be missing during testing
  if (oldPlaylist) {
    const oldVersion = oldPlaylist.get('version', null);
    const oldOptimisticVersion = oldPlaylist.get('optimisticVersion', null);
    if (oldVersion === oldOptimisticVersion) {
      yield put(D.Actions.Playlist.deleteOptimisticPlaylistData(playlistId));
    } else if (
      D.Utils.Entity.willClobberOptimisticTrackEdits(oldVersion, oldOptimisticVersion, newVersion)
    ) {
      // if the server sends a version of the playlist that's older than the optimistic version
      // ..then there's a race condition on the server, so instead of clobbering our optimistic changes
      // ..wait 1 second and retry the getTracks call until we run out of retries
      // (note savePlaylistNoAnnotations reducer also detects this and avoids clobbering optimistic edits)
      const request = getIn(action, ['params', 'content', 'request'], null);
      if (request) {
        yield delay(1000);
        yield tryGetTracksForPlaylist(
          playlistId,
          request.playlistVersion,
          request.offset,
          request.limit,
        );
        yield put(D.Actions.Playlist.updateOptimisticGetTracksRetry(playlistId));
      }
    }
  }
}

function* handleResponseAndRefresh(response, apiName, endIndex) {
  if (!response) {
    throw new Error(apiName + ' failed');
  }
  if (response.status && response.status === 'OK') {
    const { pandoraId, version, totalTracks } = response;
    // refresh page that includes endIndex, and the pages above and below immediately
    const totalPages = Math.ceil(totalTracks / PLAYLIST_PAGE_SIZE);
    const endOffset = getPageNormalizedOffset(endIndex, totalPages, PLAYLIST_PAGE_SIZE);
    // page where track was moved/inserted
    if (endOffset >= 0) {
      yield fork(
        tryGetTracksForPlaylist,
        pandoraId,
        endOffset === 0 ? 0 : version,
        endOffset,
        PLAYLIST_PAGE_SIZE,
      );
    }
    // page above
    const pageAboveOffset = endOffset - PLAYLIST_PAGE_SIZE;
    if (pageAboveOffset >= 0) {
      yield fork(
        tryGetTracksForPlaylist,
        pandoraId,
        pageAboveOffset === 0 ? 0 : version,
        pageAboveOffset,
        PLAYLIST_PAGE_SIZE,
      );
    }
    // page below
    const pageBelowOffset = endOffset + PLAYLIST_PAGE_SIZE;
    if (pageBelowOffset >= 0 && pageBelowOffset < totalTracks) {
      yield fork(
        tryGetTracksForPlaylist,
        pandoraId,
        pageBelowOffset === 0 ? 0 : version,
        pageBelowOffset,
        PLAYLIST_PAGE_SIZE,
      );
    }
    yield spawn(updateRecommendationsForPlaylist, response.pandoraId);
  } else {
    const message = response.message ? response.message : 'none';
    throw new Error(apiName + ' returned with error: ' + message);
  }
}

function* deleteTrack({ pandoraId, playlistVersion, trackItemIds, trackIndexes }) {
  const previousTracks = yield select(D.Utils.Music.getTrackObjectsForPlaylist, pandoraId);
  const previousTotalTracks = yield select(D.Utils.Music.getPlaylistTotalTracks, pandoraId);
  let response;
  try {
    yield put(
      D.Actions.Playlist.makeOptimisticDelete({
        pandoraId,
        playlistVersion,
        trackItemIds,
      }),
    );
    response = yield D.tryCall(deleteTracks, {
      pandoraId,
      playlistVersion,
      trackItemIds,
      trackIndexes,
    });
  } catch (e) {
    yield put(
      D.Actions.Playlist.rollbackOptimisticEdit({
        previousTracks,
        previousTotalTracks,
        id: pandoraId,
      }),
    );
  }
  if (response.errorType) {
    yield put(
      D.Actions.Playlist.rollbackOptimisticEdit({
        previousTracks,
        previousTotalTracks,
        id: pandoraId,
      }),
    );
  }
  return response;
}

export function* deleteTrackFromPlaylist(action) {
  const { playlistId, playlistVersion, itemId, trackIndex } = action.data;
  const response = yield call(deleteTrack, {
    pandoraId: playlistId,
    playlistVersion,
    trackItemIds: [itemId],
    trackIndexes: [trackIndex],
  });
  yield handleResponseAndRefresh(response, 'deleteTracks', trackIndex);

  const isDeletingCurrentTrack = yield select(
    D.getInDomain('playback.selectors.isItemIdPlayingInSource'),
    { sourceId: playlistId, itemId },
  );
  if (isDeletingCurrentTrack) {
    yield put(
      D.getInDomain('playback.actions.playCurrentItemForSource')({
        sourceId: playlistId,
      }),
    );
  }
}

function* editTrack({
  pandoraId,
  playlistVersion,
  inserts,
  moves,
  optimisticInserts,
  optimisticMoves,
}) {
  const optimisticEditData = {
    inserts: optimisticInserts,
    moves: optimisticMoves,
    id: pandoraId,
  };
  const previousTracks = yield select(D.Utils.Music.getTrackObjectsForPlaylist, pandoraId);
  const previousTotalTracks = yield select(D.Utils.Music.getPlaylistTotalTracks, pandoraId);
  let response;
  try {
    yield put(D.Actions.Playlist.makeOptimisticEdit(optimisticEditData));
    response = yield call(editTracks, {
      pandoraId,
      playlistVersion,
      inserts,
      moves,
    });
  } catch (e) {
    yield put(
      D.Actions.Playlist.rollbackOptimisticEdit({
        previousTracks,
        previousTotalTracks,
        id: pandoraId,
      }),
    );
  }
  // TODO: handle at api lib level that responses with errorTypes actually throw
  // exceptions
  if (response && response.errorType) {
    yield put(
      D.Actions.Playlist.rollbackOptimisticEdit({
        previousTracks,
        previousTotalTracks,
        id: pandoraId,
      }),
    );
  }
  return response;
}

export function* getTracksForPlaylist(action) {
  const { pandoraId } = action.data;
  let playlistVersion = action.data.version;

  if (playlistVersion === null) {
    const { endAction, timeout } = yield race({
      endAction: take([
        D.Constants.Api.ApiActions.PLAYLISTS_ANNOTATE_PLAYLISTS_SUCCESS,
        D.Constants.Api.ApiActions.PLAYLISTS_ANNOTATE_PLAYLISTS_FAIL,
      ]),
      timeout: delay(3000),
    });

    if (
      !timeout &&
      endAction.type === D.Constants.Api.ApiActions.PLAYLISTS_ANNOTATE_PLAYLISTS_SUCCESS
    ) {
      playlistVersion = yield select(
        D.getInDomain('catalog.utils.getVersionForPlaylist'),
        pandoraId,
      );
    }
  }

  const args = {
    pandoraId,
    playlistVersion,
    offset: action.data.offset || 0,
    limit: action.data.limit || 100,
    annotationLimit: action.data.annotationLimit || action.data.limit || 100,
    allowedTypes: D.Constants.SourceTypes.PlaylistSupportedTypes,
  };

  const response = yield D.tryCall(getTracks, args);
  if (!response) throw new Error('getTracksForPlaylist failed');
  if (getIn(response, 'entities.playlists.undefined.errorType') === 'PLAYLIST_VERSION_MISMATCH') {
    yield call(
      D.putAndWait,
      D.Actions.Playlist.annotatePlaylists({ pandoraIds: [args.pandoraId] }),
    );
    yield put(D.Actions.Playlist.clearTracksForPlaylist(args.pandoraId));
  }
}

export function* insertTrackInPlaylist(action) {
  const pandoraId = action.data.playlistId;
  const playlistVersion = action.data.playlistVersion;
  const inserts = [insertTrackDto(action.data.trackId, action.data.trackIndex)];
  const response = yield call(editTrack, {
    pandoraId,
    playlistVersion,
    inserts,
    optimisticInserts: inserts,
  });

  if (response) {
    // For an insertion, it is as if we're moving the last track to its insertion index
    yield put(D.Actions.Modal.openModalCoachmark(messages.onInsertTrackSuccessMessage));
    yield call(handleResponseAndRefresh, response, 'editTrack', action.data.trackIndex);
  }
}

export function* moveTrackInPlaylist(action) {
  const { playlistId, playlistVersion, oldTrackIndex, newTrackIndex, itemId, tracks } = action.data;

  const moves = [moveTrackDto(itemId, oldTrackIndex, newTrackIndex)];
  const optimisticMoves = createPlaylistTrackMoves(oldTrackIndex, newTrackIndex, tracks);
  const response = yield call(editTrack, {
    pandoraId: playlistId,
    playlistVersion,
    moves,
    optimisticMoves,
  });
  if (response) {
    yield call(handleResponseAndRefresh, response, 'editTrack', newTrackIndex);
  }
}

export function* getAutofillTracksForPlaylist(action) {
  const args = {
    playlistId: action.data.playlistId,
    numSongs: action.data.numSongs,
    annotationLimit: 0,
  };
  const getTracksResponse = yield D.tryCall(getPlaylistAutofillTracks, args);
  if (!getTracksResponse) throw new Error('getAutofillTracksForPlaylist failed getting tracks');

  const playlists = getTracksResponse.entities.playlists;
  if (playlists[action.data.playlistId]) {
    const trackList = playlists[action.data.playlistId].autoFillSongs;
    if (trackList) {
      const itemPandoraIds = trackList.map(item => item.musicId);
      const addTracksArgs = {
        data: {
          pandoraId: action.data.playlistId,
          playlistVersion: action.data.playlistVersion,
          itemPandoraIds,
        },
      };
      yield appendItemsToPlaylist(addTracksArgs);
    } else {
      throw new Error('getAutofillTracksForPlaylist returned response with no track list');
    }
  }
}

export function* getRecommendationsForPlaylist(action) {
  const args = {
    playlistId: action.data.pandoraId,
    addedSongs: action.data.addedSongs || [],
    removedSongs: action.data.removedSongs || [],
    numSongs: action.data.numSongs,
    start: action.data.start || 0,
    annotationLimit: action.data.annotationLimit || action.data.numSongs,
  };
  const response = yield D.tryCall(getPlaylistRecommendations, args);
  if (!response) throw new Error('getRecommendationsForPlaylist failed');
}

// returns a plain object to be passed directly into rollbackOptimisticEdit action creator
function* getPlaylistRollbackData(playlist, trackId) {
  const result = { id: null, trackId };
  if (playlist) {
    result.id = playlist.get('pandoraId');
    result.previousTracks = yield select(D.Utils.Music.getTrackObjectsForPlaylist, result.id);
    result.previousTotalTracks = yield select(D.Utils.Music.getPlaylistTotalTracks, result.id);
  }
  return result;
}

function* handleEndActionForPlaylistFeedback(
  action,
  successActions,
  failActions,
  playlist,
  playlistRollbackData,
  trackId,
) {
  // wait for ending success/fail action
  const endAction = yield take([...successActions, ...failActions]);
  if (successActions.includes(endAction.type) && actionsMatch(action, endAction)) {
    yield updatePlaylistForFeedback(playlist, trackId, true);
  } else if (failActions.includes(endAction.type)) {
    yield revertPlaylistForFeedback(playlist, playlistRollbackData);
  }
}

export function* preUpdatePlaylistForFeedback(action) {
  const { trackToken, isPositive } = action.payload;

  if (
    trackToken &&
    D.Utils.SourceType.getPandoraTypeFromPandoraId(trackToken) !==
      D.Constants.SourceTypes.PandoraTypes.EPISODE
  ) {
    const trackEntity = yield select(D.getInDomain('playback.utils.getDetailedTrack'), trackToken);
    if (!trackEntity) return;
    const stationId = trackEntity.get('stationId', null);
    const trackId = trackEntity.get('pandoraId');

    // linked Playlist
    const linkedPlaylist = yield select(D.Utils.Music.findLinkedPlaylist, stationId);
    const trackIndex =
      linkedPlaylist && D.Utils.Music.findTrackIndexInPlaylist(linkedPlaylist, trackId);
    const linkedPlaylistRollbackData = yield getPlaylistRollbackData(linkedPlaylist, trackId);

    // my thumps up playlist
    const mtuPlaylist = yield select(D.Utils.Music.findMyThumbsUpPlaylist);
    const mtuTrackIndex =
      mtuPlaylist && D.Utils.Music.findTrackIndexInPlaylist(mtuPlaylist, trackId);
    const mtuPlaylistRollbackData = yield getPlaylistRollbackData(mtuPlaylist, trackId);

    if (linkedPlaylistRollbackData.id || mtuPlaylistRollbackData.id) {
      try {
        if (isPositive) {
          // to add a track, we have to have both playlist data and the track missing from the playlist
          const addingLinkedTrack = trackIndex < 0 && !!linkedPlaylistRollbackData.id;
          const addingMtuTrack = mtuTrackIndex < 0 && !!mtuPlaylistRollbackData.id;
          if (addingLinkedTrack || addingMtuTrack) {
            if (action.type === D.Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK) {
              if (addingLinkedTrack) {
                yield put(
                  D.Actions.Playlist.intentAddTrackForFeedback({
                    playlistId: linkedPlaylistRollbackData.id,
                    trackId,
                  }),
                );
              }
              if (addingMtuTrack) {
                yield put(
                  D.Actions.Playlist.intentAddTrackForFeedback({
                    playlistId: mtuPlaylistRollbackData.id,
                    trackId,
                  }),
                );
              }
              // wait for ending success/fail action for a few seconds
              const { endAction, timeout } = yield race({
                endAction: take([
                  D.Constants.Api.ApiActions.ADD_FEEDBACK_SUCCESS,
                  D.Constants.Api.ApiActions.ADD_FEEDBACK_FAIL,
                  D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS,
                  D.Constants.Api.ApiActions.ACTION_THUMB_UP_FAIL,
                ]),
                timeout: delay(3000),
              });
              if (timeout) {
                log.error(
                  'timeout: ADD_FEEDBACK result not returned within 3 seconds, affecting linked playlist: ' +
                    linkedPlaylistRollbackData.id +
                    ' and/or MTU playlist: ' +
                    mtuPlaylistRollbackData.id +
                    ' for track: ' +
                    trackId,
                );
                if (addingLinkedTrack) {
                  yield updatePlaylistForFeedback(linkedPlaylist, trackId, false);
                }
                if (addingMtuTrack) {
                  yield updatePlaylistForFeedback(mtuPlaylist, trackId, false);
                }
              } else if (
                [
                  D.Constants.Api.ApiActions.ADD_FEEDBACK_SUCCESS,
                  D.Constants.Api.ApiActions.ACTION_THUMB_UP_SUCCESS,
                ].includes(endAction.type)
              ) {
                if (actionsMatch(action, endAction)) {
                  if (addingLinkedTrack) {
                    yield updatePlaylistForFeedback(linkedPlaylist, trackId, true);
                  }
                  if (addingMtuTrack) {
                    yield updatePlaylistForFeedback(mtuPlaylist, trackId, true);
                  }
                }
              } else if (
                [
                  D.Constants.Api.ApiActions.ADD_FEEDBACK_FAIL,
                  D.Constants.Api.ApiActions.ACTION_THUMB_UP_FAIL,
                ].includes(endAction.type)
              ) {
                if (addingLinkedTrack) {
                  yield revertPlaylistForFeedback(linkedPlaylist, linkedPlaylistRollbackData);
                }
                if (addingMtuTrack) {
                  yield revertPlaylistForFeedback(mtuPlaylist, mtuPlaylistRollbackData);
                }
              }
            }
          } else {
            // removing a thumb up for a track should remove the track from linked playlist, not mtu
            if (
              linkedPlaylist &&
              linkedPlaylistRollbackData.id &&
              action.type === D.Constants.UseCases.ActionTypes.REMOVE_TRACK_FEEDBACK &&
              trackIndex >= 0
            ) {
              const trackItemId = linkedPlaylist.getIn(['tracks', trackIndex, 'itemId']);
              yield put(
                D.Actions.Playlist.intentDeleteTrackForFeedback({
                  playlistId: linkedPlaylistRollbackData.id,
                  trackItemId,
                }),
              );
              yield handleEndActionForPlaylistFeedback(
                action,
                [
                  D.Constants.Api.ApiActions.DELETE_FEEDBACK_SUCCESS,
                  D.Constants.Api.ApiActions.ACTION_REMOVE_THUMB_SUCCESS,
                ],
                [
                  D.Constants.Api.ApiActions.DELETE_FEEDBACK_FAIL,
                  D.Constants.Api.ApiActions.ACTION_REMOVE_THUMB_FAIL,
                ],
                linkedPlaylist,
                linkedPlaylistRollbackData,
                trackId,
              );
            }
          }
        } else {
          // adding a thumb down should remove the track from linked playlist, not mtu
          if (
            linkedPlaylist &&
            linkedPlaylistRollbackData.id &&
            action.type === D.Constants.UseCases.ActionTypes.ADD_TRACK_FEEDBACK &&
            trackIndex >= 0
          ) {
            const trackItemId = linkedPlaylist.getIn(['tracks', trackIndex, 'itemId']);
            yield put(
              D.Actions.Playlist.intentDeleteTrackForFeedback({
                playlistId: linkedPlaylistRollbackData.id,
                trackItemId,
              }),
            );
            yield handleEndActionForPlaylistFeedback(
              action,
              [
                D.Constants.Api.ApiActions.ADD_FEEDBACK_SUCCESS,
                D.Constants.Api.ApiActions.ACTION_THUMB_DOWN_SUCCESS,
              ],
              [
                D.Constants.Api.ApiActions.ADD_FEEDBACK_FAIL,
                D.Constants.Api.ApiActions.ACTION_THUMB_DOWN_FAIL,
              ],
              linkedPlaylist,
              linkedPlaylistRollbackData,
              trackId,
            );
          }
        }
      } catch (err) {
        if (linkedPlaylistRollbackData.id) {
          yield put(D.Actions.Playlist.rollbackOptimisticEdit(linkedPlaylistRollbackData));
          yield put(
            D.Actions.Playlist.deleteOptimisticPlaylistData({
              id: linkedPlaylistRollbackData.id,
            }),
          );
        }
        if (mtuPlaylistRollbackData.id) {
          yield put(D.Actions.Playlist.rollbackOptimisticEdit(mtuPlaylistRollbackData));
          yield put(
            D.Actions.Playlist.deleteOptimisticPlaylistData({
              id: mtuPlaylistRollbackData.id,
            }),
          );
        }
      }
    }
  }
}

export function* redirectToNewPlaylist(action) {
  try {
    const pandoraId = action.response.tempPandoraId;
    const urlPath = action.response.entities.playlists[pandoraId].shareableUrlPath;
    yield put(push(urlPath));
  } catch (error) {
    throw new Error('redirectToNewPlaylist failed', error);
  }
}

export function* redirectToMyMusicPlaylistPage(action) {
  if (action.params.actionParams.redirectToMyMusicPlaylists) {
    yield put(push(D.Utils.Route.getCollectionPath() + '/playlists'));
  }
}

function* revertPlaylistForFeedback(playlist, optimisticRollbackData) {
  log.trace('revertPlaylistForFeedback for playlist: ' + optimisticRollbackData.id);
  yield put(D.Actions.Playlist.rollbackOptimisticEdit(optimisticRollbackData));
  yield put(
    D.Actions.Playlist.deleteOptimisticPlaylistData({
      id: optimisticRollbackData.id,
    }),
  );
  yield put(D.Actions.Playlist.onFeedbackUpdateCompletion({ playlist }));
}

function* updatePlaylistForFeedback(playlist, trackId, addedFeedbackSuccessfully) {
  if (playlist) {
    const pandoraId = playlist.get('pandoraId');
    log.trace('updatePlaylistForFeedback track: ' + trackId + ' in playlist: ' + pandoraId);
    yield put(D.Actions.Playlist.onFeedbackUpdateCompletion({ playlist }));
    const trackIndex = D.Utils.Music.findTrackIndexInPlaylist(playlist, trackId);
    const processedTrackIndex = trackIndex >= 0 ? trackIndex : 0;
    const playlistVersion = playlist.get('version');
    const playlistOptimisticVersion = playlist.get('optimisticVersion') || 0;
    let responseVersion = playlistVersion;
    if (addedFeedbackSuccessfully) {
      responseVersion = Math.max(playlistOptimisticVersion, playlistVersion + 1);
    }
    const response = {
      pandoraId,
      version: responseVersion,
      totalTracks: playlist.get('totalTracks'),
      status: 'OK',
    };
    yield handleResponseAndRefresh(response, 'updatePlaylistForFeedback', processedTrackIndex);
  }
}

// this is called after edits to preload recommendations for empty playlist backstage search
export function* updateRecommendationsForPlaylist(pandoraId) {
  yield D.tryCall(getRecommendationsForPlaylist, {
    data: { pandoraId, numSongs: 100 },
  });
}

function* refreshPotentiallyStaleTracklist(playlist) {
  yield call(
    D.putAndWait,
    D.Actions.Playlist.annotatePlaylists({
      pandoraIds: [playlist.get('pandoraId')],
    }),
  );
  const newVersion = yield select(
    D.getInDomain('catalog.utils.getVersionForPlaylist'),
    playlist.get('pandoraId'),
  );
  if (newVersion !== playlist.get('version')) {
    yield put(D.Actions.Playlist.clearTracksForPlaylist(playlist.get('pandoraId')));
  }
}

export function* onTrackStarted({ payload: { type, id } }) {
  if (type === D.Constants.SourceTypes.SourceTypes.PLAYLIST) {
    const playlist = yield select(D.Utils.Music.getPlaylistFromState, id);
    if (playlist && playlist.get('tracks') && !playlist.get('tracks').isEmpty()) {
      yield call(refreshPotentiallyStaleTracklist, playlist);
    }
  }
}

export function* onVisibilityChange() {
  if (!D.Utils.PageVisibility.isHidden()) {
    const currentSourceId = yield select(
      D.getInDomain('playback.selectors.getCurrentItemSourceId', ''),
    );
    if (
      D.Utils.SourceType.getPandoraTypeFromPandoraId(currentSourceId) ===
      D.Constants.SourceTypes.PandoraTypes.PLAYLIST
    ) {
      const playbackPlaylist = yield select(D.Utils.Music.getPlaylistFromState, currentSourceId);
      if (playbackPlaylist) {
        yield call(refreshPotentiallyStaleTracklist, playbackPlaylist);
      }
    }

    const currentPath = yield select(D.Utils.Route.getCurrentPathnameFromState);
    const { pandoraId, sourceType } = yield select(
      D.Utils.Route.getPandoraIdAndSourceTypeFromBackstagePath,
      currentPath,
    );
    if (
      pandoraId !== currentSourceId &&
      sourceType === D.Constants.SourceTypes.SourceTypes.PLAYLIST
    ) {
      const backstagePlaylist = yield select(D.Utils.Music.getPlaylistFromState, pandoraId);
      if (backstagePlaylist) {
        yield call(refreshPotentiallyStaleTracklist, backstagePlaylist);
      }
    }
  }
}

export function* onLocationChange(action) {
  const { pathname } = action.payload;

  if (isNowPlayingPlaylistRoute(pathname)) {
    const pandoraId = yield select(D.getInDomain('playback.utils.getCurrentPlaylist'));

    if (pandoraId && D.Utils.SourceType.isPlaylistPandoraId(pandoraId)) {
      yield call(D.putAndWait, D.Actions.Playlist.annotatePlaylists({ pandoraIds: [pandoraId] }));
    }
  }
}
